import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";
import { equipmentProofPath, type EquipmentProofFile } from "@/lib/oprema";
import { EquipmentProofLink } from "./EquipmentProofLink";
import { dohvatiDokumente } from "@/lib/dokumenti";

/** Equipment rows reference stored files; document links require their archive parent IDs. */
export function useEquipmentProofs(
  workerId: string,
  enabled: boolean,
  revision: number,
) {
  const { can } = useAuth();
  const archive = can("dokument", "read");
  const [proofs, setProofs] = useState<{ key: string; files: Record<string, string> } | null>(null);
  const key = `${workerId}/${enabled}/${archive}/${revision}`;
  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    if (!archive) {
      void api.get<EquipmentProofFile[]>(equipmentProofPath(workerId), { signal: controller.signal })
        .then(({ data }) => {
          if (!controller.signal.aborted) setProofs({ key, files: Object.fromEntries(data.map(file => [file.dokument_id, file.employee_document_id])) });
        }).catch(() => { /* Out-of-scope receipts stay unavailable. */ });
      return () => controller.abort();
    }
    void dohvatiDokumente(workerId, controller.signal, false, false)
      .then(async (documents) => {
        const entries = await Promise.all(
          documents
            .filter(
              (document) =>
                ["potvrda-primitka-ozo", "ppe_handover"].includes(
                  document.document_type_code ?? "",
                ) && document.status !== "deleted",
            )
            .map(async (document) => {
              const files = (
                await api.get<{ dokument_id: string }[]>(
                  `/radnici/${workerId}/documents/${document.id}/files`,
                  { signal: controller.signal },
                )
              ).data;
              return files.map((file) => [file.dokument_id, document.id]);
            }),
        );
        if (!controller.signal.aborted)
          setProofs({ key, files: Object.fromEntries(entries.flat()) });
      })
      .catch(() => {
        /* StructuredEvidence remains available if file resolution fails. */
      });
    return () => controller.abort();
  }, [workerId, enabled, revision, archive, key]);
  return enabled && proofs?.key === key ? proofs.files : {};
}

/** Load each linked image through the authorized document content endpoint. */
export function EquipmentEvidencePhotos({
  workerId,
  documentId,
}: {
  workerId: string;
  documentId: string;
}) {
  const { can } = useAuth();
  const archive = can("dokument", "read");
  const allowed = archive || can("oprema", "create");
  const base = archive ? `/radnici/${workerId}/documents/${documentId}` : equipmentProofPath(workerId, documentId);
  const key = `${workerId}/${documentId}/${allowed}/${archive}`;
  const [evidence, setEvidence] = useState<{
    key: string;
    photos: string[];
    error: string;
  } | null>(null);
  const current = evidence?.key === key ? evidence : null;
  useEffect(() => {
    if (!allowed) return;
    const controller = new AbortController();
    const urls: string[] = [];
    void api
      .get<{ id: string; employee_document_id?: string }[]>(
        archive ? `${base}/files` : equipmentProofPath(workerId),
        { signal: controller.signal },
      )
      .then(async (response) => {
        for (const file of response.data.filter(file => archive || file.employee_document_id === documentId)) {
          const content = await api.get<Blob>(
            `${base}/files/${file.id}/content`,
            { responseType: "blob", signal: controller.signal },
          );
          if (controller.signal.aborted) return;
          if (["image/jpeg", "image/png"].includes(content.data.type))
            urls.push(URL.createObjectURL(content.data));
        }
        if (!controller.signal.aborted) {
          setEvidence({ key, photos: [...urls], error: "" });
        }
      })
      .catch((cause) => {
        if (!controller.signal.aborted) {
          urls.splice(0).forEach((url) => URL.revokeObjectURL(url));
          setEvidence({
            key,
            photos: [],
            error: errorMessage(cause, "Fotografije i potpis nisu dostupni. Pokušajte ponovno."),
          });
        }
      });
    return () => {
      controller.abort();
      urls.forEach((url) => URL.revokeObjectURL(url));
      setEvidence(null);
    };
  }, [workerId, documentId, allowed, archive, base, key]);
  if (!allowed) return null;
  return (
    <section aria-label="Fotke primopredaje" className="space-y-2">
      <p className="font-semibold">Fotke i potpis na potvrdi</p>
      {!current && <p role="status">Učitavanje fotografija i potpisa…</p>}
      {current?.error && <p role="status">{current.error}</p>}
      <div className="flex flex-wrap gap-3">
        {current?.photos.map((src, index) => (
          <EquipmentProofLink workerId={workerId} documentId={documentId} key={src}>
            <img
              className="h-24 w-28 rounded border object-contain"
              src={src}
              alt={`Dokaz primopredaje ${index + 1}`}
            />
          </EquipmentProofLink>
        ))}
      </div>
      <EquipmentProofLink
        className="inline-flex min-h-11 items-center underline"
        workerId={workerId}
        documentId={documentId}
      >
        Otvori potvrdu
      </EquipmentProofLink>
    </section>
  );
}
